import { useAuthContext } from '../contexts/AuthContext';
import type { AuthError, AuthRole } from '../types/auth';

/**
 * Custom hook for authentication
 * Provides a simplified interface to auth functionality
 */
export const useAuth = () => {
  const {
    isAuthenticated,
    token,
    user,
    isLoading,
    login,
    logout,
    verifyToken
  } = useAuthContext();

  /** Tokens issued before roles existed carry no role; treat them as client sessions */
  const role: AuthRole = user?.role ?? 'client';
  const isAdmin = role === 'admin';

  /**
   * Attempt to login with a username and password
   * @param username - Account username
   * @param password - Account password
   * @param loginRole - Credential set to authenticate against (defaults to 'client')
   * @returns Promise that resolves if successful, rejects with AuthError if failed
   */
  const signIn = async (
    username: string,
    password: string,
    loginRole: AuthRole = 'client'
  ): Promise<void> => {
    if (!username || username.trim() === '') {
      const error: AuthError = {
        message: 'Username is required',
      };
      throw error;
    }

    if (!password || password.trim() === '') {
      const error: AuthError = {
        message: 'Password is required',
      };
      throw error;
    }

    await login(username.trim(), password, loginRole);
  };

  /**
   * Attempt to login against the admin credential set
   */
  const signInAsAdmin = async (username: string, password: string): Promise<void> => {
    await signIn(username, password, 'admin');
  };

  /**
   * Sign out the current user
   */
  const signOut = (): void => {
    logout();
  };

  /**
   * Check if current session is valid
   * @returns true if authenticated, false otherwise
   */
  const checkAuth = async (): Promise<boolean> => {
    if (!isAuthenticated || !token) {
      return false;
    }

    try {
      await verifyToken();
      return true;
    } catch {
      return false;
    }
  };

  return {
    // State
    isAuthenticated,
    isLoading,
    user,
    token,
    role,
    isAdmin,

    // Methods
    signIn,
    signInAsAdmin,
    signOut,
    checkAuth,
  };
};
